import {camelCase, endsWith, snakeCase} from 'lodash';
import * as PreferenceApi from 'api/PreferenceApi';
import {addCustomCoverImage, mkImageSelection} from 'ducks/coverPageImages';
import {updateSectionValue} from 'ducks/reportSections';
import {toggleNotification} from 'ducks/showNotification';
import {changeTheme} from 'ducks/theme';
import {loadUserSettings} from 'ducks/userSettings';

export function makePreferences(preferences, sectionKey, url, key, value) {
  let urlKey = url ? `[${url}]` : '';
  let prefix = `${camelCase(sectionKey)}${urlKey}`;
  let preferenceKey = `${prefix}.${key}`;
  preferences[preferenceKey] = typeof value !== 'undefined' ? `${value}` : null;
  return preferences;
}

export function updatePreferences(preferences, reportType, message) {
  return async (dispatch) => {
    await dispatch(toggleNotification(true, message));
    await PreferenceApi.updateV2Preferences(preferences, reportType);
    return dispatch(toggleNotification(false));
  };
}

function parsePreferenceValue(keyPath, value) {
  if (endsWith(keyPath, '.included')) {
    return value === 'true';
  }
  if (value && endsWith(keyPath, '.index')) {
    return parseInt(value, 10);
  }
  if (value && endsWith(keyPath, '.order')) {
    return value.split(',');
  }
  return value;
}

function handleCoverPageImagesPreference({dispatch, keyPath, reportType, value}) {
  if (/^coverPageImages.selection$/.test(keyPath)) {
    const selection = value
      ? JSON.parse(value)
      : {};

    dispatch(mkImageSelection({
      reportType,
      ...selection,
    }));
  } else if (/^coverPageImages.custom\..+$/.test(keyPath) && /^([^@]+)@.+$/.test(value)) {
    const valueMatch = value.match(/^([^@]+)@(.+)$/);
    const type = valueMatch[1];
    const imageUri = valueMatch[2];
    dispatch(addCustomCoverImage({
      imageUri,
      type,
    }));
  }
}

export function loadPreferences() {
  return (dispatch) => {
    const promises = ['cma', 'rental'].map(async reportType => {
      const {data = []} = await PreferenceApi.getV2Preferences(reportType);
      const preferencePromises = data.map(preference => {
        const keyPath = preference.key;
        const value = parsePreferenceValue(keyPath, preference.value);
        if ((/^theme\.(\w+)$/).test(keyPath)) {
          const match = keyPath.match(/^theme\.(\w+)$/);
          return dispatch(changeTheme({
            [match[1]]: value,
          }));
        } else if (/^(coverPageImages\..+|layout|(half|large).*CoverPageImage)$/.test(keyPath)) {
          return handleCoverPageImagesPreference({
            dispatch,
            keyPath,
            reportType,
            value,
          });
        } else if (keyPath === 'notesTitle') {
          return dispatch(loadUserSettings({notesTitle: value}));
        } else if (keyPath.startsWith('show.current')) {
          return dispatch(loadUserSettings({ [keyPath]: value }));
        } else if (!/^[a-zA-Z_]+Map.included$/.test(keyPath)) { // map included preferences have been removed
          return dispatch(updateSectionValue({
            value,
            reportType,
            key: getSectionName(keyPath),
            url: getPdfUrl(keyPath),
            dataKeyPath: getSubKeyPath(keyPath),
          }));
        } else {
          // ignore preference
          return Promise.resolve();
        }
      });
      return Promise.all(preferencePromises);
    });
    return Promise.all(promises);
  };
}

function getSectionName(keyPath) {
  if (keyPath.indexOf('custom[') === 0) {
    return 'CUSTOM';
  } else {
    const sectionName = keyPath.substr(0, keyPath.indexOf('.'));
    return snakeCase(sectionName).toUpperCase();
  }
}

function getSubKeyPath(keyPath) {
  return keyPath.indexOf('custom[') === 0 ?
    keyPath.substr(keyPath.indexOf(']') + 2) :
    keyPath.substr(keyPath.indexOf('.') + 1);
}

function getPdfUrl(keyPath) {
  if (keyPath.indexOf('custom[') === 0) {
    const urlStart = keyPath.indexOf('[') + 1;
    const urlEnd = keyPath.indexOf(']');
    return keyPath.substr(urlStart, urlEnd - urlStart);
  } else {
    return undefined;
  }
}
